import { MMA_CENTS } from './tax-calendar.js';
/**
 * Individuali veikla (a person working on their own, e.g. a Bolt, Uber or Wolt driver): the journal's
 * categories and the year's taxes worked out from it. These are estimates for planning and for the GPM311
 * declaration; the person (or their accountant) checks them. Sources, read 2026-10-08:
 * - GPM on IV profit from 2026: 20 % less a credit (P ≤ 20 000 €: 15 % of P; up to 42 500 €: P × (0.15 − 2/300 000 × (P − 20 000));
 *   above: none, and the income joins the person's other income at the general rates). 2025 and before: 15 % less
 *   P × 0.10, tapering to nothing at 35 000 €. (GPMĮ art. 6, as amended 2025-06; finmin.lrv.lt, KPMG 2025-12.)
 * - VSD 12.52 % and PSD 6.98 % of 90 % of the taxable profit, together at most 43 × VDU a year; a person not insured
 *   otherwise pays at least 6.98 % of the MMA in PSD each month (sodra.lt "Vykdau individualią veiklą", 2026 leaflet).
 * - Verslo liudijimas: a fixed GPM paid for the certificate; VSD 8.72 % and PSD 6.98 % of the MMA for each month
 *   it is valid; income up to 50 000 € a year from 2026 (45 000 € before).
 * - Bolt and other platforms: the income is the whole fare; the commission is an expense (VMI's explanation for
 *   Bolt drivers). A platform in another EU country makes its commission subject to reverse-charge VAT (21 %).
 */
export const IV_PLATFORMS = ['bolt', 'uber', 'wolt', 'bolt_food', 'other'];
/** Journal categories (labels are the panel's Lithuanian source strings). */
export const IV_CATEGORIES = {
    platform: { direction: 'in', kind: 'income', label: 'Pajamos per platformą (Bolt, Uber, Wolt…)' },
    sale: { direction: 'in', kind: 'income', label: 'Pajamos už paslaugas' },
    other_income: { direction: 'in', kind: 'income', label: 'Kitos veiklos pajamos' },
    platform_fee: { direction: 'out', kind: 'expense', label: 'Platformos komisinis mokestis' },
    fuel: { direction: 'out', kind: 'expense', label: 'Kuras ir elektra' },
    car: { direction: 'out', kind: 'expense', label: 'Automobilio remontas, draudimas, plovimas' },
    car_rent: { direction: 'out', kind: 'expense', label: 'Automobilio nuoma ar lizingas' },
    phone: { direction: 'out', kind: 'expense', label: 'Telefonas ir internetas' },
    other_expense: { direction: 'out', kind: 'expense', label: 'Kitos veiklos išlaidos' },
    sodra: { direction: 'out', kind: 'tax', label: 'Sodros įmoka (VSD, PSD)' },
    gpm: { direction: 'out', kind: 'tax', label: 'GPM į VMI' },
};
/** A paid invoice of the account (not entered by hand). */
const INVOICE = { direction: 'in', kind: 'income', label: 'Apmokėta sąskaita' };
export const ivCategory = (id) => IV_CATEGORIES[id] ?? INVOICE;
/** Average monthly wage (VDU) used for Sodra's ceilings, in cents. */
export const VDU_CENTS = { 2025: 210_888, 2026: 231_215 };
const known = (table, year) => {
    const years = Object.keys(table).map(Number).sort((a, b) => a - b);
    return table[year] ?? table[years.filter((y) => y <= year).pop() ?? years[0]];
};
export const VSD_RATE = 0.1252;
export const PSD_RATE = 0.0698;
export const VAT_REVERSE_RATE = 0.21;
/** Above this income a year (this and the year before) a person must register for VAT. */
export const VAT_THRESHOLD_CENTS = 4_500_000;
export const vlIncomeLimit = (year) => (year >= 2026 ? 5_000_000 : 4_500_000);
/** GPM on IV taxable profit (cents) for a year, with the rule of that year. */
export function ivGpm(profit, year) {
    if (profit <= 0)
        return { tax: 0, rate: 0, overLimit: false };
    const p = profit / 100;
    const [rate, base, top] = year >= 2026 ? [0.2, 0.15, 42_500] : [0.15, 0.1, 35_000];
    const credit = p <= 20_000 ? p * base : p <= top ? p * (base - (2 / 300_000) * (p - 20_000)) : 0;
    const tax = Math.round((p * rate - credit) * 100);
    return { tax, rate: tax / profit, overLimit: p > top };
}
/** Months of the year the activity runs (from its start, to December or to this month for the current year). */
function activeMonths(year, startedOn, today) {
    const first = startedOn && startedOn.slice(0, 4) === String(year) ? Number(startedOn.slice(5, 7)) : startedOn && startedOn.slice(0, 4) > String(year) ? 13 : 1;
    const last = today.slice(0, 4) === String(year) ? Number(today.slice(5, 7)) : today.slice(0, 4) < String(year) ? 0 : 12;
    return Math.max(0, last - first + 1);
}
/** The year's figures from the journal. `today` (Vilnius date) limits the months of the current year. */
export function ivYear(year, entries, settings, today = new Date().toISOString().slice(0, 10)) {
    const sum = (kind, filter = () => true) => entries.filter((e) => ivCategory(e.category).kind === kind && filter(e)).reduce((s, e) => s + e.amount, 0);
    const income = sum('income');
    const expensesActual = sum('expense');
    const flat = settings.expenseMethod === 'flat';
    const expenses = flat ? Math.round(income * 0.3) : expensesActual;
    const profit = Math.max(0, income - expenses);
    const months = activeMonths(year, settings.startedOn, today);
    const mma = known(MMA_CENTS, year);
    const ceiling = 43 * known(VDU_CENTS, year);
    let sodraBase = 0, vsd = 0, psd = 0, psdMinimum = 0, gpmProfit = 0, gpm = { tax: 0, rate: 0, overLimit: false };
    if (settings.activity === 'vl') {
        // The certificate's GPM is paid when it is issued; Sodra from the MMA for each month it is valid.
        vsd = Math.round(mma * 0.0872) * months;
        psd = Math.round(mma * PSD_RATE) * months;
    }
    else {
        sodraBase = Math.min(Math.round(profit * 0.9), ceiling);
        vsd = Math.round(sodraBase * VSD_RATE);
        psdMinimum = settings.insuredElsewhere ? 0 : Math.round(mma * PSD_RATE) * months;
        psd = Math.max(Math.round(sodraBase * PSD_RATE), psdMinimum);
        gpmProfit = Math.max(0, profit - (flat ? 0 : vsd + psd));
        gpm = ivGpm(gpmProfit, year);
    }
    const paid = { sodra: sum('tax', (e) => e.category === 'sodra'), gpm: sum('tax', (e) => e.category === 'gpm') };
    const platformFees = sum('expense', (e) => e.category === 'platform_fee');
    const byMonth = Array.from({ length: 12 }, (_, i) => {
        const month = `${year}-${String(i + 1).padStart(2, '0')}`;
        const inMonth = (e) => e.day.startsWith(month);
        return { month, income: sum('income', inMonth), expenses: sum('expense', inMonth) };
    });
    const next = year + 1;
    return {
        year, activity: settings.activity, expenseMethod: settings.expenseMethod,
        income, expensesActual, expenses, profit, sodraBase, vsd, psd, psdMinimum, months,
        gpmProfit, gpm: gpm.tax, gpmRate: gpm.rate, gpmOverLimit: gpm.overLimit,
        paid, due: { sodra: Math.max(0, vsd + psd - paid.sodra), gpm: Math.max(0, gpm.tax - paid.gpm) },
        platformFees, reverseVat: Math.round(platformFees * VAT_REVERSE_RATE),
        vatThresholdNear: income >= VAT_THRESHOLD_CENTS * 0.8, vlOverLimit: settings.activity === 'vl' && income > vlIncomeLimit(year),
        byMonth,
        deadlines: settings.activity === 'vl'
            ? [{ what: 'Sodros įmokos už verslo liudijimą (kas ketvirtį)', due: `${next}-01-15` }, { what: 'GPM312 metinė deklaracija', due: `${next}-05-01` }]
            : [
                ...(psdMinimum ? [{ what: 'PSD kas mėnesį (ne mažiau kaip 6,98 % MMA)', due: 'kiekvieno mėnesio paskutinė diena' }] : []),
                { what: 'GPM311 deklaracija ir GPM sumokėjimas', due: `${next}-05-01` },
                { what: 'VSD ir PSD už metus (jei nemokėta kas mėnesį)', due: `${next}-05-01` },
            ],
    };
}
//# sourceMappingURL=iv.js.map